

import type { Game } from "../types/games";

// placeholder art for titles that have no real menu screenshots yet
import menuImage from "../assets/coming-soon.svg";

/* The twelve captures are the menu's own tabs, in the menu's own order.
   Named after what each one actually shows: the previous four were guesses,
   and two were plain wrong — "Visuals" pointed at the Items page and "Config"
   pointed at Triggerbot. */
/* Cropped to the menu window (cs2-NN.webp). The csteal originals kept a strip
   of the game behind them down the right edge and in the rounded corners. */
import csAimbot from "../assets/pictures/cs2 external/cs2-01.webp";
import csTriggerbot from "../assets/pictures/cs2 external/cs2-02.webp";
import csPlayers from "../assets/pictures/cs2 external/cs2-03.webp";
import csItems from "../assets/pictures/cs2 external/cs2-04.webp";
import csProjectiles from "../assets/pictures/cs2 external/cs2-05.webp";
import csBomb from "../assets/pictures/cs2 external/cs2-06.webp";
import csSpectators from "../assets/pictures/cs2 external/cs2-07.webp";
import csChams from "../assets/pictures/cs2 external/cs2-08.webp";
import csGrenades from "../assets/pictures/cs2 external/cs2-09.webp";
import csRadar from "../assets/pictures/cs2 external/cs2-10.webp";
import csMisc from "../assets/pictures/cs2 external/cs2-11.webp";
import csConfig from "../assets/pictures/cs2 external/cs2-12.webp";

import thumbDeadlock from "../assets/thumbnails/generated/thumb-deadlock.webp";
import thumbCs2 from "../assets/thumbnails/generated/thumb-cs2.webp";
import thumbFivem from "../assets/thumbnails/generated/thumb-fivem.webp";
import thumbRoblox from "../assets/thumbnails/generated/thumb-roblox.webp";
import thumbApex from "../assets/thumbnails/generated/thumb-apex.webp";
import thumbRust from "../assets/thumbnails/generated/thumb-rust.webp";
/* 640px versions of the same artwork. The full 1376px files were being sent
   to cards that show them 180-370px wide -- 284 KB for the homepage's four
   tiles alone, loading early enough to compete with the hero. srcset lets the
   browser pick the smallest that stays sharp on the screen in front of it. */
import thumbDeadlockSm from "../assets/thumbnails/generated/thumb-deadlock-640.webp";
import thumbCs2Sm from "../assets/thumbnails/generated/thumb-cs2-640.webp";
import thumbFivemSm from "../assets/thumbnails/generated/thumb-fivem-640.webp";
import thumbRobloxSm from "../assets/thumbnails/generated/thumb-roblox-640.webp";
import thumbApexSm from "../assets/thumbnails/generated/thumb-apex-640.webp";
import thumbRustSm from "../assets/thumbnails/generated/thumb-rust-640.webp";

import dlAimbot from "../assets/menu-teal/product/dl-aimbot.webp";
import dlVisuals from "../assets/menu-teal/product/dl-visuals.webp";
import dlVisuals2 from "../assets/menu-teal/product/dl-visuals2.png";
import dlScripts from "../assets/menu-teal/product/dl-scripts.webp";
import fmEsp from "../assets/menu-teal/product/fm-esp.webp";
import fmTeleport from "../assets/menu-teal/product/fm-teleport.webp";
import fmVehicle from "../assets/menu-teal/product/fm-vehicle.webp";
import fmMisc from "../assets/menu-teal/product/fm-misc.webp";

const AimbotIcon = (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
    />
  </svg>
);

const VisualsIcon = (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const ScriptsIcon = (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
    />
  </svg>
);

const ConfigIcon = (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

export const GAMES: Game[] = [
  {
    id: "deadlock-external",
    /* Display name only. The id stays deadlock-external because it is the
       key for the route, PRODUCT_FEATURES, the pricing map and the backend
       product; renaming it would break all four. */
    name: "Deadlock Internal + External",
    type: "external",
    tagline: "Our most established build",
    description:
      "High-performance external cheat for Deadlock with advanced ESP, Aimbot and Scripts.",
    pricing: {
      weekly: "€5",
      monthly: "€10",
      lifetime: "€150",
    },
    features: [
      "Full feature access",
      "Automatic updates",
      "Community support",
      "Instant activation",
    ],
    uniqueFeatures: ["Silent aimbot", "Full ESP suite", "Hero scripts"],
    dropdownImage: thumbDeadlock,
    dropdownImageSmall: thumbDeadlockSm,
    featureTabs: [
      {
        id: "aimbot",
        name: "Aimbot",
        description:
          "Advanced Aimbot for Player's & Souls with custom FOV, smoothing, visiblity check and bone selection for unmatched precision.",
        image: dlAimbot,
        icon: AimbotIcon,
      },
      {
        id: "visuals",
        name: "Visuals",
        description:
          "Real-time ESP with customizable overlays, player info, health bars, and distance indicators.",
        image: dlVisuals,
        icon: VisualsIcon,
      },
      {
        id: "visuals2",
        name: "Visuals 2",
        description:
          "Extended visual features including radar, crosshair customization, and advanced overlay options.",
        image: dlVisuals2,
        icon: VisualsIcon,
      },
      {
        id: "scripts",
        name: "Scripts",
        description:
          "Automated gameplay scripts for movement, abilities, and tactical advantages.",
        image: dlScripts,
        icon: ScriptsIcon,
      },
    ],
  },

  {
    id: "cs2-external",
    name: "CS2 External",
    type: "external",
    tagline: "In open beta, priced accordingly",
    // Everything below is read off the real menu screenshots in
    // assets/pictures/cs2 external. There is no skin changer and no
    // inventory manager in the build, so neither is advertised.
    description:
      "External for Counter-Strike 2. Aimbot and triggerbot with autowall and min-damage filters, player and item ESP, chams, grenade helper and radar.",
    pricing: {
      weekly: "€5",
      monthly: "€10",
    },
    features: [
      "Aimbot and triggerbot",
      "Player, item and projectile ESP",
      "Automatic updates",
      "Community support",
      "Instant activation",
    ],
    uniqueFeatures: ["Smart aimbot", "Autowall", "Standalone RCS"],
    badge: "Beta",
    dropdownImage: thumbCs2,
    dropdownImageSmall: thumbCs2Sm,
    featureTabs: [
      {
        id: "aimbot",
        name: "Aimbot",
        description:
          "Key, FOV, smoothing, target bones, visible-only, autowall, a min-damage threshold and predictive aim, with a drawn FOV circle and standalone recoil control.",
        image: csAimbot,
        icon: AimbotIcon,
      },
      {
        id: "triggerbot",
        name: "Triggerbot",
        description:
          "Key, delay in milliseconds, smart triggerbot and prediction, filtered by autowall, min damage and autostop.",
        image: csTriggerbot,
        icon: AimbotIcon,
      },
      {
        id: "players",
        name: "Players",
        description:
          "Boxes, skeletons and hitboxes with occlusion, fill, outline and thickness, plus health and ammo bars, weapon ESP and off-screen arrows.",
        image: csPlayers,
        icon: VisualsIcon,
      },
      {
        id: "items",
        name: "Items",
        description:
          "Dropped weapons by icon, name and ammo out to a max distance, filtered by rifles, SMGs, shotguns, snipers, pistols, heavy, grenades and utility.",
        image: csItems,
        icon: VisualsIcon,
      },
      {
        id: "projectiles",
        name: "Projectiles",
        description:
          "Icons, names and timer bars for grenades in flight, inferno bounds, and a colour per nade type.",
        image: csProjectiles,
        icon: VisualsIcon,
      },
      {
        id: "bomb",
        name: "Bomb",
        description:
          "Bomb ESP with a timer bar and defuse indicator, coloured for ticking, defusing and defused, plus a bomb timer HUD.",
        image: csBomb,
        icon: VisualsIcon,
      },
      {
        id: "spectators",
        name: "Spectators",
        description: "Shows who is watching you, with its own text colour.",
        image: csSpectators,
        icon: VisualsIcon,
      },
      {
        id: "chams",
        name: "Chams",
        description:
          "Player chams with a material picker, an enemies-only switch and separate handling for players you cannot see.",
        image: csChams,
        icon: VisualsIcon,
      },
      {
        id: "grenades",
        name: "Grenades",
        description:
          "Grenade prediction with a drawn path, local-only mode and a path colour.",
        image: csGrenades,
        icon: ScriptsIcon,
      },
      {
        id: "radar",
        name: "Radar",
        description:
          "Radar showing name, health and weapon, able to centre on the scoreboard key, with CT and T colours and an aspect setting.",
        image: csRadar,
        icon: ScriptsIcon,
      },
      {
        id: "misc",
        name: "Misc",
        description:
          "No-flash, active-feature display, an FPS cap, the menu accent and toggle key, and uninject.",
        image: csMisc,
        icon: ScriptsIcon,
      },
      {
        id: "config",
        name: "Config",
        description:
          "Save and reload named configs, with Rage V1, Rage V2, Legit and Default presets included.",
        image: csConfig,
        icon: ConfigIcon,
      },
    ],
  },
  {
    id: "fivem-external",
    name: "FiveM External",
    type: "external",
    tagline: "The largest menu we ship",
    description:
      "Feature-rich external menu for FiveM with ESP, teleportation, vehicle spawning, and more. Perfect for roleplay servers.",
    pricing: {
      weekly: "€10",
      monthly: "€15",
    },
    features: [
      "Full feature access",
      "Cloud config sync",
      "Automatic updates",
      "Community support",
      "Instant activation",
    ],
    uniqueFeatures: ["Silent aim", "Vehicle control", "60+ teleports"],
    dropdownImage: thumbFivem,
    dropdownImageSmall: thumbFivemSm,
    featureTabs: [
      {
        id: "esp",
        name: "ESP",
        description:
          "See players, vehicles, and objects through walls. Customizable ESP with distance indicators.",
        image: fmEsp,
        icon: VisualsIcon,
      },
      {
        id: "teleport",
        name: "Teleport",
        description:
          "Instant teleportation to waypoints, players, or saved locations. Never waste time traveling.",
        image: fmTeleport,
        icon: ScriptsIcon,
      },
      {
        id: "vehicle",
        name: "Vehicle Menu",
        description:
          "Spawn any vehicle, modify colors, tune performance, and manage vehicle options.",
        image: fmVehicle,
        icon: ScriptsIcon,
      },
      {
        id: "misc",
        name: "Misc Features",
        description:
          "God mode, infinite stamina, super jump, and various quality of life improvements.",
        image: fmMisc,
        icon: ConfigIcon,
      },
    ],
  },
  {
    id: "roblox-external",
    name: "Roblox External",
    type: "external",
    tagline: "External only, no script execution",
    description:
      "External aim and visuals for Roblox. Because it runs fully external it does not execute Lua, so there is no script hub: what you get is aimbot, silent aim and ESP.",
    pricing: {
      weekly: "€10",
      monthly: "€20",
    },
    features: [
      "Aimbot and silent aim",
      "Player ESP",
      "Automatic updates",
      "Instant activation",
    ],
    uniqueFeatures: ["Aimbot", "Silent aim", "ESP"],
    badge: "Beta",
    dropdownImage: thumbRoblox,
    dropdownImageSmall: thumbRobloxSm,
    featureTabs: [
      {
        id: "aimbot",
        name: "Aimbot",
        description:
          "Target selection with FOV and smoothing controls, plus silent aim that leaves your own cursor alone.",
        image: menuImage,
        icon: AimbotIcon,
      },
      {
        id: "visuals",
        name: "Visuals",
        description:
          "Player ESP with boxes, healthbars and distance, drawn externally over the game.",
        image: menuImage,
        icon: VisualsIcon,
      },
    ],
  },
  {
    id: "apex-external",
    name: "Apex Legends External",
    type: "external",
    tagline: "Details when it's ready",
    // No feature list yet: nothing is decided, so nothing is claimed here.
    description:
      "In development. The feature list will be published once the build is settled.",
    pricing: {
      weekly: "€10",
      monthly: "€20",
    },
    features: [],
    status: "in-development",
    dropdownImage: thumbApex,
    dropdownImageSmall: thumbApexSm,
    featureTabs: [],
  },
  {
    id: "rust-external",
    name: "Rust External",
    type: "external",
    tagline: "Details when it's ready",
    // No feature list yet: nothing is decided, so nothing is claimed here.
    description:
      "In development. The feature list will be published once the build is settled.",
    pricing: {
      weekly: "€10",
      monthly: "€20",
    },
    features: [],
    status: "in-development",
    dropdownImage: thumbRust,
    dropdownImageSmall: thumbRustSm,
    featureTabs: [],
  },
];

/** srcset for a game's artwork: the 640px and full 1376px versions. */
export const thumbSrcSet = (game: Game): string | undefined =>
  game.dropdownImage && game.dropdownImageSmall
    ? `${game.dropdownImageSmall} 640w, ${game.dropdownImage} 1376w`
    : undefined;

export const getGameById = (id: string): Game | undefined => {
  return GAMES.find((game) => game.id === id);
};

export const getGamesByType = (type: "internal" | "external"): Game[] => {
  return GAMES.filter((game) => game.type === type);
};

export const getAllGameIds = (): string[] => {
  return GAMES.map((game) => game.id);
};
